import { BankAccountTypeId } from 'app/constants/bank-accounts';
import { CreditNoteStatusId } from 'app/constants/creditNotes';
import { InvoiceStatusId } from 'app/constants/invoices';
import { PayoutStatusId } from 'app/constants/payment';
import { Country, StateLabel } from 'app/hooks/i18n/useGetCountryDetails';
import i18n from 'app/services/i18n';

import { TypeDto } from './apiClient/generated/types';
import { SemanticBillState } from './apiClient/settlement/types';

export const bankAccountTypeTranslation: Record<BankAccountTypeId, string> = {
  [BankAccountTypeId.Check]: i18n.t('common:bankAccountTypes.check', 'Check'),
  [BankAccountTypeId.Wire]: i18n.t('common:bankAccountTypes.wire', 'Wire'),
  [BankAccountTypeId.Ach]: i18n.t('common:bankAccountTypes.ach', 'ACH'),
  [BankAccountTypeId.EfsMoneyCode]: i18n.t('common:bankAccountTypes.efsMoneyCode', 'EFS Money Code'),
  [BankAccountTypeId.TriumphFuelCard]: i18n.t('common:bankAccountTypes.triumphFuelCard', 'Triumph Fuel Card'),
  [BankAccountTypeId.TChek]: i18n.t('common:bankAccountTypes.tChek', 'T-CHEK'),
  [BankAccountTypeId.FuelCard]: i18n.t('common:bankAccountTypes.fuelCard', 'Fuel Card'),
  [BankAccountTypeId.CadAch]: i18n.t('common:bankAccountTypes.cadAch', 'CAD ACH'),
  [BankAccountTypeId.CadCheck]: i18n.t('common:bankAccountTypes.cadCheck', 'CAD Check'),
  [BankAccountTypeId.SameDayAch]: i18n.t('common:bankAccountTypes.sameDayAch', 'Same Day ACH'),
  [BankAccountTypeId.DraftAch]: i18n.t('common:bankAccountTypes.draftAch', 'Draft ACH'),
  [BankAccountTypeId.DraftCadAch]: i18n.t('common:bankAccountTypes.draftCadAch', 'CAD Draft ACH'),
  [BankAccountTypeId.IntlWire]: i18n.t('common:bankAccountTypes.intlWire', 'Intl Wire'),
  [BankAccountTypeId.MxnAch]: i18n.t('common:bankAccountTypes.mxnAch', 'MXN ACH'),
  [BankAccountTypeId.DraftMxnAch]: i18n.t('common:bankAccountTypes.draftMxnAch', 'Draft Mexican ACH'),
  [BankAccountTypeId.EurWire]: i18n.t('common:bankAccountTypes.eurWire', 'EUR Wire'),
  [BankAccountTypeId.DraftEurWire]: i18n.t('common:bankAccountTypes.draftEurWire', 'Draft Euro Wire'),
  [BankAccountTypeId.ReverseWire]: i18n.t('common:bankAccountTypes.reverseWire', 'Reverse USD Wire'),
  [BankAccountTypeId.LoadPay]: i18n.t('common:bankAccountTypes.loadPay', 'LoadPay'),
};

export const billStateTranslation: Record<SemanticBillState, string> = {
  approved: i18n.t('common:billStates.approved', 'Approved'),
  delivered: i18n.t('common:billStates.delivered', 'Delivered'),
  exception: i18n.t('common:billStates.exception', 'Exception'),
  ignored: i18n.t('common:billStates.ignored', 'Ignored'),
  incomplete: i18n.t('common:billStates.incomplete', 'Incomplete'),
  pending: i18n.t('common:billStates.pending', 'Pending'),
  rejected: i18n.t('common:billStates.rejected', 'Rejected'),
  removed: i18n.t('common:billStates.removed', 'Removed'),
  sent: i18n.t('common:billStates.sent', 'Sent'),
  submitted: i18n.t('common:billStates.submitted', 'Submitted'),
};

export const countryTranslation: Record<Country, string> = {
  [Country.Canada]: i18n.t('common:countries.canada', 'Canada'),
  [Country.Mexico]: i18n.t('common:countries.mexico', 'Mexico'),
  [Country.UnitedStates]: i18n.t('common:countries.unitedStates', 'United States'),
};

export const creditNoteStatusTranslation: Record<CreditNoteStatusId, string> = {
  [CreditNoteStatusId.AppliedInFull]: i18n.t('common:creditNoteStatus.appliedInFull', 'Applied In Full'),
  [CreditNoteStatusId.Held]: i18n.t('common:creditNoteStatus.held', 'Held'),
  [CreditNoteStatusId.ImportError]: i18n.t('common:creditNoteStatus.importError', 'Import Error'),
  [CreditNoteStatusId.Outstanding]: i18n.t('common:creditNoteStatus.outstanding', 'Outstanding'),
  [CreditNoteStatusId.Pending]: i18n.t('common:creditNoteStatus.pending', 'Pending'),
  [CreditNoteStatusId.Removed]: i18n.t('common:creditNoteStatus.removed', 'Removed'),
};

const invoiceStatusTranslation: Record<InvoiceStatusId, string> = {
  [InvoiceStatusId.Approved]: i18n.t('common:invoiceStatus.approved', 'Approved'),
  [InvoiceStatusId.Held]: i18n.t('common:invoiceStatus.held', 'Held'),
  [InvoiceStatusId.MissingBank]: i18n.t('common:invoiceStatus.missingBank', 'Missing Bank'),
  [InvoiceStatusId.MissingCarrier]: i18n.t('common:invoiceStatus.missingCarrier', 'Missing Carrier'),
  [InvoiceStatusId.Paid]: i18n.t('common:invoiceStatus.paid', 'Paid'),
  [InvoiceStatusId.PendingReissue]: i18n.t('common:invoiceStatus.pendingReissue', 'Pending Reissue'),
  [InvoiceStatusId.Pending]: i18n.t('common:invoiceStatus.pending', 'Pending'),
  [InvoiceStatusId.Removed]: i18n.t('common:invoiceStatus.removed', 'Removed'),
};

export const getInvoiceStatusName = (status: TypeDto): null | string => {
  if (!status.name) {
    return null;
  }

  if (status.name === 'Processing') {
    return i18n.t('common:invoiceStatus.processing', 'Processing');
  }

  return invoiceStatusTranslation[status.id as InvoiceStatusId] ?? status.name;
};

export const paymentStatusTranslation: Record<PayoutStatusId, string> = {
  [PayoutStatusId.Initializing]: i18n.t('common:paymentStatus.initializing', 'Initializing'),
  [PayoutStatusId.Paid]: i18n.t('common:paymentStatus.paid', 'Paid'),
  [PayoutStatusId.Pending]: i18n.t('common:paymentStatus.pending', 'Pending'),
  [PayoutStatusId.Removed]: i18n.t('common:paymentStatus.removed', 'Removed'),
};

export const stateLabelTranslation: Record<StateLabel, string> = {
  [StateLabel.Province]: i18n.t('common:stateLabels.province', 'Province'),
  [StateLabel.State]: i18n.t('common:state', 'State'),
};
